import { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import type { PublishedDoc } from '../model/types';
import type { PublicClient } from '../backend/types';
import { siteTitle } from '../model/catalog';
import { SLUG_RE } from '../model/plans';
import { errorMessage } from '../lib/util';
import { Logo, useTitle } from '../components/ui';
import { SiteFrame } from '../site/SiteView';

type State = { s: 'loading' } | { s: 'missing' } | { s: 'error'; msg: string } | { s: 'ok'; doc: PublishedDoc };

/**
 * A couple's published website, as guests see it. `standalone` means the page was opened directly (a guest
 * following a link), so links to Kovio itself load the full app rather than switching pages in place.
 */
export function PublicSite({ client, standalone = false }: { client: PublicClient; standalone?: boolean }) {
  const { slug = '' } = useParams();
  const backend = client;
  const [state, setState] = useState<State>({ s: 'loading' });
  const [attempt, setAttempt] = useState(0);
  useTitle(state.s === 'ok' ? `${siteTitle(state.doc.site)} · Our wedding` : state.s === 'missing' ? 'Kovio · Website not found' : 'Kovio');

  useEffect(() => {
    if (!SLUG_RE.test(slug)) { setState({ s: 'missing' }); return; }
    let live = true;
    setState({ s: 'loading' });
    backend.getPublished(slug)
      .then((doc) => { if (live) setState(doc ? { s: 'ok', doc } : { s: 'missing' }); })
      .catch((e) => { if (live) setState({ s: 'error', msg: errorMessage(e, 'Check your connection and try again.') }); });
    return () => { live = false; };
  }, [backend, slug, attempt]);

  if (state.s === 'ok') {
    const doc = state.doc;
    return <SiteFrame className="guest-view pub-view" site={doc.site} mode="live" label={siteTitle(doc.site)} submitRsvp={(r) => backend.submitRsvp(doc.slug, r)} />;
  }
  if (state.s === 'loading') return <div className="view v-loading"><div><div className="spinner" /></div></div>;
  return (
    <div className="view">
      <div className="pub-state">
        <div>
          <HomeLink standalone={standalone} className="brand-btn" label="Kovio home page"><Logo /></HomeLink>
          {state.s === 'missing' ? (
            <>
              <h1>We couldn’t find this wedding website</h1>
              <p>Check the address on your invitation. If the couple has only just shared it, it may not be published yet.</p>
            </>
          ) : (
            <>
              <h1>This website didn’t load</h1>
              <p>{state.msg}</p>
              <p><button type="button" className="btn btn-primary" onClick={() => setAttempt((a) => a + 1)}>Try again</button></p>
            </>
          )}
          <HomeLink standalone={standalone} className="btn">Make your own wedding website</HomeLink>
        </div>
      </div>
    </div>
  );
}

function HomeLink({ standalone, className, label, children }: { standalone: boolean; className: string; label?: string; children: React.ReactNode }) {
  return standalone
    ? <a href="/" className={className} aria-label={label}>{children}</a>
    : <Link to="/" className={className} aria-label={label}>{children}</Link>;
}

export function NotFound() {
  useTitle('Kovio · Page not found');
  return (
    <div className="view">
      <div className="pub-state">
        <div>
          <Link to="/" className="brand-btn" aria-label="Kovio home page"><Logo /></Link>
          <h1>This page doesn’t exist</h1>
          <p>The link may be mistyped, or the page has moved.</p>
          <Link className="btn btn-primary" to="/">Go to the home page</Link>
        </div>
      </div>
    </div>
  );
}
